import { Log, User, UserManagerSettings } from "oidc-client";
import ChangePasswordModel from "../../Components/Account/Models/ChangePasswordModel";
import ActivateUser from "../../Components/Auth/Models/ActivateUser";
import { configService } from "../Config/ConfigService";
import ChangeEmailModel from "../../Components/Account/Models/ChangeEmailModel";
import AuthSessionStorage from "./AuthSessionStorage";
import UserManagerSingleton from './UserManagerSingleton';
import { AccessTokenExpiringNotificationTime } from "../../Components/Auth/SessionTimeout";

export default class AuthService {

    constructor(errorPage: string) {
        this.errorPage = errorPage;
    }

    authUrl?: string;
    clientId?: string;
    errorPage: string;
    onBehalfOfHeader: string = "oboPhysicianId";
    userManager : UserManagerSingleton;

    initialize = async () => {
        var configuration = await configService.getConfiguration();
        this.authUrl = configuration.AbpnIdentity.Authority;
        this.clientId = configuration.AbpnIdentity.ClientId;

        const settings : UserManagerSettings = {
            authority: this.authUrl,
            client_id: this.clientId,
            redirect_uri: configuration.AbpnIdentity.RedirectUri,
            post_logout_redirect_uri: configuration.AbpnIdentity.PostLogoutRedirectUri,
            response_type: "code",
            automaticSilentRenew: false,
            accessTokenExpiringNotificationTime: AccessTokenExpiringNotificationTime,
            loadUserInfo: false,
            scope: configuration.AbpnIdentity.Scope,
            userStore: AuthSessionStorage.getInitUserStore(configuration)
        };

        this.userManager = UserManagerSingleton.getInstance(settings);

        // Logger
        Log.logger = console;
        Log.level = Log.DEBUG;

        this.userManager.getOidcUserManager().events.addUserLoaded(() => {
            if (window.location.href.indexOf("signin-oidc") !== -1) {
                this.navigateToScreen();
            }
        });

        this.userManager.getOidcUserManager().events.addSilentRenewError((e) => {
            console.error("silent renew error", e.message);
        });

    }

    signinRedirectCallback = () => {
        this.userManager.getOidcUserManager().signinRedirectCallback().then(() => {
            var redirectUrl = localStorage.getItem("redirectUri");
            window.location.replace(redirectUrl);
        });
    };


    getUser = async () : Promise<User | null> => {
        const user = await this.userManager.getOidcUserManager().getUser();
        if (!user) {
            return await this.userManager.getOidcUserManager().signinRedirectCallback();
        }

        return user;
    };

    getAccessToken = async () => {
        var user = await this.getUser();
        return user && user.access_token;
    }

    setOnBehalfOf = (physicianId: string | null) => {
        if (physicianId) {
            AuthSessionStorage.getStorageMedia().setItem(this.onBehalfOfHeader, physicianId);
        }
    }

    getOnBehalfOf = () => AuthSessionStorage.getStorageMedia().getItem(this.onBehalfOfHeader);

    parseJwt = (token: string) => {
        const base64Url = token.split(".")[1];
        const base64 = base64Url.replace("-", "+").replace("_", "/");
        return JSON.parse(window.atob(base64));
    };

    signinRedirect = (emailPrefill: string = null, redirectUri: string = null) => {
        if (redirectUri) {
            localStorage.setItem("redirectUri", redirectUri);
        }

        if (AuthSessionStorage.getStorageMedia().getItem(this.onBehalfOfHeader)) {
            AuthSessionStorage.getStorageMedia().removeItem(this.onBehalfOfHeader);
        }

        this.userManager.getOidcUserManager().signinRedirect({ login_hint: emailPrefill });
    };

    staffSigninRedirect = () => {
        localStorage.setItem("redirectUri", window.location.href);
        this.userManager.getOidcUserManager().signinRedirect({ acr_values: "idp:aad" });
    };

    navigateToScreen = () => {
        window.location.replace("/en/dashboard");
    };

    isAuthenticated = () => {
        console.log('authenticating...');
        const oidcStorage = AuthSessionStorage.getOidcSession();

        return (!!oidcStorage && !!oidcStorage.access_token)
    };

    getSessionExpiration = () : number => {
        const oidc = AuthSessionStorage.getOidcSession();
        if (!oidc) return null;
        return oidc.expires_at;
    }

    signinSilent = () => {
        this.userManager.getOidcUserManager().signinSilent()
            .then((user) => {
                console.log("signed in", user);
            })
            .catch((err) => {
                console.error(err);
            });
    };

    signinSilentCallback = () => {
        this.userManager.getOidcUserManager().signinSilentCallback();
    };

    createSigninRequest = () => {
        return this.userManager.getOidcUserManager().createSigninRequest();
    };

    logout = async () => {
        const user = await this.userManager.getOidcUserManager().getUser();
        await this.userManager.getOidcUserManager().signoutRedirect({
            id_token_hint: user?.id_token
        });
        await this.userManager.getOidcUserManager().clearStaleState();
    };

    changeEmail = (model: ChangeEmailModel) => {
        return fetch(this.authUrl + "/account/ChangeEmail",
        {
            method: "post",
            headers: [['Pragma', 'no-cache'], ["Content-Type", "application/json"]],
            body: JSON.stringify(model)
        });
    }

    changePassword = (model: ChangePasswordModel) => {
        return fetch(this.authUrl + "/account/ChangePassword",
        {
            method: "post",
            headers: [['Pragma', 'no-cache'], ["Content-Type", "application/json"]],
            body: JSON.stringify(model)
        });
    }
    
    register = (model: ActivateUser) => {
        return fetch(this.authUrl + "/account/Register",
            {
                method: "post",
                headers: [['Pragma', 'no-cache'], ["Content-Type", "application/json"]],
                body: JSON.stringify(model)
            })
            .then(response => {
                return response.ok;
            })
            .catch(error => {
                window.location.href = this.errorPage;
                return Promise.reject(error);
            });
    }

}